import { useFocusEffect, useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useEffect, useState } from 'react';
import { FlatList, Image, SafeAreaView, StyleSheet, View } from 'react-native';
import { Divider } from 'react-native-elements';

import AVATARS from '@/assets/avatars';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { NoPatient } from '@/components/PatientList/NoPatient';
import { BodyWrapper } from '@/components/ui/therapist/BodyWrapper';
import { Button } from '@/components/ui/therapist/Button';
import Card from '@/components/ui/therapist/Card';
import { Icon } from '@/components/Icon';
import { Input } from '@/components/ui/therapist/Input';
import { Modal } from '@/components/ui/therapist/Modal';
import { Text } from '@/components/ui/therapist/Text';
import { useResponsiveHeight } from '@/components/Util/reactResponsiveDimensions';
import colors, { appTheme } from '@/config/colors';
import { useDashboardContext } from '@/contexts/DashboardContext/useDashboardContext';
import { useOnHover } from '@/hooks/useOnHover';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { hasInternetAsync } from '@/screens/HomeTherapist/helpers';
import store, { useStoreActions, useStoreState } from '@/stores/store';
import { logError } from '@/utils/logHandling';
import { replacedPatientProfileHeader } from '@/utils/stringHelpers';

import { styles } from './styles';
import {
  showDigaPatientWarning,
  showSelfPayerWarning,
} from '@/utils/licenseCheck';
import {
  getAllPatientsLicenseStatus,
  PatientLicenseStatus,
} from '@/api/services/patient';
import { Patient } from '@/stores/store.types';
import { mergeArraysById } from '@/utils/arrays';

export function PatientList() {
  const listHeight = useResponsiveHeight(67);

  const [isDemoTherapistVisible, setIsDemoTherapistVisible] = useState(false);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [filterValue, setFilterValue] = useState<string>('');

  const [enhancedPatients, setEnhancedPatients] = useState<
    (Patient & {
      license: PatientLicenseStatus | null;
    })[]
  >([]);

  const { patients, changeID, changeSelectedPatient } = useDashboardContext();
  const { isHovered, handleHoverIn, handleHoverOut } = useOnHover();
  const { setShowLoadingSpinner } = store.getActions();

  const { isTherapistDemo } = useStoreState((store) => store);
  const { navigate } =
    useNavigation<StackNavigationProp<TherapistStackParamList>>();
  const { setGlobalPopupState } = useStoreActions((actions) => ({
    setGlobalPopupState: actions.setGlobalPopupState,
  }));

  const handleAddPatient = useCallback(async () => {
    if (isTherapistDemo) {
      setIsDemoTherapistVisible(true);
      return;
    } else {
      try {
        const hasInternet = await hasInternetAsync();
        if (hasInternet) {
          navigate('AddPatientScreen');
        } else {
          setGlobalPopupState({
            headerText: 'Keine Verbindung zum neolexon Server',
            bodyText:
              'Überprüfen Sie Ihre Internetverbindung und versuchen es erneut.',
            leftButton: '',
            rightButton: '',
          });
        }
      } catch (error) {
        logError('handleAddPatient in SideBar', error);
      }
    }
  }, [hasInternetAsync, navigate]);

  const fetchLicenseStatus = useCallback(async () => {
    try {
      if (!patients) {
        return;
      }

      const licenseStatus = await getAllPatientsLicenseStatus();
      const licenseStatusForPatients = mergeArraysById(
        patients,
        licenseStatus ?? [],
        '_id',
        'patientId',
      );

      setEnhancedPatients(licenseStatusForPatients);
    } catch (error) {
      logError('Failed to fetch license status for all patients', error);
    }
  }, [patients]);

  useFocusEffect(
    useCallback(() => {
      fetchLicenseStatus();

      return () => {
        setShowFilter(false);
        setFilterValue('');
      };
    }, [fetchLicenseStatus]),
  );

  useEffect(() => {
    if (!patients) {
      setShowLoadingSpinner(true);
    } else {
      setShowLoadingSpinner(false);
    }
  }, [patients]);

  return (
    <BodyWrapper scrollEnabled={false}>
      <View style={styles.sideBar}>
        {patients?.length === 0 && (
          <NoPatient handleAddPatient={handleAddPatient} />
        )}
        {patients && patients.length > 0 ? (
          <>
            <View style={styles.buttonWrapper}>
              <Button
                icon="filter"
                label="Filter"
                look="tertiary"
                onPress={() => {
                  setShowFilter(!showFilter);
                  if (showFilter) {
                    setFilterValue('');
                  }
                }}
                onHoverIn={handleHoverIn}
                onHoverOut={handleHoverOut}
                style={[
                  styles.filterButton,
                  isHovered && {
                    opacity: 0.7,
                    backgroundColor: colors.newUI.therapist.blue20,
                  },
                ]}
              />
              {showFilter ? (
                <View style={{ minWidth: 185, marginRight: 16 }}>
                  <Input
                    value={filterValue}
                    onChange={(text) => setFilterValue(text)}
                    label={''}
                    style={{ margin: 0 }}
                    placeholder="Patient:in suchen"
                  />
                </View>
              ) : null}
              <Button
                look="primary"
                icon={'addUser'}
                onPress={handleAddPatient}
                label="Patient:in hinzufügen "
              />
            </View>
            <Card styleBody={{ padding: 0 }}>
              <SafeAreaView style={{ maxHeight: listHeight }}>
                <FlatList
                  scrollEnabled={true}
                  data={enhancedPatients.filter((patient) =>
                    (patient.firstName + patient.lastName)
                      .toLowerCase()
                      .includes(filterValue.toLowerCase()),
                  )}
                  keyExtractor={(item) => item._id}
                  renderItem={({ item }) => {
                    const name = item.firstName + ' ' + item.lastName;
                    const avatarArray = item.avatar?.[0].split('/');
                    const image =
                      avatarArray?.length &&
                      avatarArray[avatarArray?.length - 1].split('.')[0];
                    const source = AVATARS[image as keyof typeof AVATARS];
                    const idStyles = StyleSheet.create({
                      backgroundColor: { backgroundColor: colors.white },
                      color: { color: appTheme.textColor },
                    });
                    return (
                      <PressableNeolexon
                        onPress={async () => {
                          try {
                            await changeID(item._id, true, patients);
                            await changeSelectedPatient(item);
                            await navigate('PatientProfile', {
                              id: item._id,
                              patients,
                              replacedTitle: replacedPatientProfileHeader({
                                patients: undefined,
                                patientId: item._id,
                                patient: item,
                              }),
                            });
                          } catch (error) {
                            logError(
                              'Change selected patient in patient list',
                              {
                                changeIdType: `changeId is typeof ${typeof changeID}`,
                                changeSelectedPatientType: `changeSelectedPatient is typeof ${typeof changeSelectedPatient}`,
                                patientsExist: patients?.length,
                                patientExists:
                                  item.hasOwnProperty('_id') ||
                                  item.hasOwnProperty('id'),
                              },
                            );
                          }
                        }}
                        style={idStyles.backgroundColor}
                      >
                        <View style={styles.userBox}>
                          {image ? (
                            <Image style={styles.userImg} source={source} />
                          ) : null}
                          <View style={{ gap: 4 }}>
                            <Text
                              numberOfLines={1}
                              ellipsizeMode="tail"
                              style={[idStyles.color]}
                            >
                              {name}
                            </Text>
                            {item.license &&
                            (showDigaPatientWarning(item.license) ||
                              showSelfPayerWarning(item.license)) ? (
                              <View
                                style={{
                                  flexDirection: 'row',
                                  alignItems: 'center',
                                }}
                              >
                                <Icon
                                  style={{ marginRight: 5 }}
                                  name="warningOutline"
                                  size={16}
                                  color={colors.newUI.therapist.orange60}
                                />
                                <Text style={{ fontSize: 14 }}>
                                  Ablauf Patienten-App
                                </Text>
                              </View>
                            ) : null}
                          </View>
                          <Icon
                            name="chevronRight"
                            size={20}
                            color={colors.newUI.therapist.primaryBlue80}
                            style={{ marginLeft: 'auto' }}
                          />
                        </View>
                        <Divider color={colors.lightGrey} width={2} />
                      </PressableNeolexon>
                    );
                  }}
                />
              </SafeAreaView>
            </Card>
          </>
        ) : null}
        <Modal
          title="In der Demo nicht möglich"
          isVisible={isDemoTherapistVisible}
          confirmActionButton={
            <Button
              label="OK"
              look="primary"
              onPress={() => setIsDemoTherapistVisible(false)}
            />
          }
          onClose={() => setIsDemoTherapistVisible(false)}
        >
          <Text style={{ fontSize: 18 }}>
            Registrieren Sie sich bei neolexon, um Ihre eigenen Patient:innen
            und Übungssets zu erstellen.
          </Text>
        </Modal>
      </View>
    </BodyWrapper>
  );
}
